import { childByClass } from '@/utils/active-page'

/**
 * Instantanés DOM des pages quittées, pour le glissé de retour.
 *
 * Le problème : pendant la traction, l'écran précédent n'existe pas. Il n'est
 * monté qu'à la navigation, c'est-à-dire au relâchement. Sous la page tirée on
 * ne découvrait donc que le fond de l'app, ce qui ne ressemble à rien.
 *
 * Les deux façons d'avoir un écran dessous coûtent cher : naviguer dès le
 * début du geste remonte la page quittée à chaque annulation, avec son
 * squelette et ses requêtes. On garde donc de côté une COPIE DU DOM de la page
 * au moment où on la quitte, et c'est elle qu'on pose sous la page pendant la
 * traction. Au relâchement, la vraie page arrive et prend sa place au pixel
 * près : l'écart avec la réalité aura duré trois cents millisecondes.
 *
 * Ce qu'une copie ne restitue pas : un `<canvas>` ou une `<video>` repart
 * vide, les animations CSS redémarrent, et les `id` sont en double le temps du
 * geste (sans conséquence, le calque est `inert` et `aria-hidden`). Les
 * positions de défilement, elles, sont recopiées à la main : `cloneNode` les
 * perd, et une liste parcourue reviendrait à son sommet.
 *
 * On ne capture que sur une poussée ANIMÉE, ce qui exclut d'office les
 * bascules d'onglet : leur instantanéité est une décision mesurée sur
 * appareil, elles n'ont pas à payer un `cloneNode` de page entière.
 */

/** Au-delà, les plus anciennes sortent. Deux retours enchaînés restent servis. */
const MAX_SNAPSHOTS = 3

/** Insertion ordonnée : la Map de JS conserve l'ordre, la plus ancienne sort en premier. */
const snapshots = new Map<string, HTMLElement>()

/** Chemin nu, sans query ni ancre : c'est la clé de rangement et de recherche. */
export function snapshotKey(path: string): string {
  return path.split('?')[0].split('#')[0]
}

/**
 * Recopie les positions de défilement, nœud à nœud. `cloneNode` copie le DOM
 * mais pas l'état.
 */
function copyScrollPositions(from: HTMLElement, to: HTMLElement) {
  const originals = from.querySelectorAll<HTMLElement>('*')
  const clones = to.querySelectorAll<HTMLElement>('*')
  for (let i = 0; i < originals.length; i++) {
    const src = originals[i]
    if (src.scrollTop || src.scrollLeft) {
      clones[i].scrollTop = src.scrollTop
      clones[i].scrollLeft = src.scrollLeft
    }
  }
  to.scrollTop = from.scrollTop
  to.scrollLeft = from.scrollLeft
}

/** Range un instantané, en évinçant le plus ancien si le quota est atteint. */
export function rememberSnapshot(path: string, el: HTMLElement): void {
  const key = snapshotKey(path)
  snapshots.delete(key)
  snapshots.set(key, el)
  while (snapshots.size > MAX_SNAPSHOTS) {
    const oldest = snapshots.keys().next().value
    if (oldest === undefined) break
    snapshots.delete(oldest)
  }
}

/** L'instantané rangé pour ce chemin, sans le consommer. */
export function findSnapshot(path: string | null | undefined): HTMLElement | null {
  if (!path) return null
  return snapshots.get(snapshotKey(path)) ?? null
}

export function clearSnapshots(): void {
  snapshots.clear()
}

/**
 * Copie la page actuellement affichée dans `outlet`. Appelé depuis la garde de
 * navigation, donc alors que le DOM montre encore la page qu'on quitte.
 */
export function capturePageSnapshot(outlet: HTMLElement | null, path: string): void {
  const page = childByClass(outlet, 'app-page')
  if (!page) return

  const clone = page.cloneNode(true) as HTMLElement
  copyScrollPositions(page, clone)
  // Un geste interrompu a pu laisser du style en ligne sur la vraie page ;
  // la copie doit repartir propre.
  clone.style.cssText = ''
  clone.setAttribute('aria-hidden', 'true')
  clone.inert = true
  rememberSnapshot(path, clone)
}
